import { olderGeneration, youngerGeneration } from '@/assets/messages';
import {
  AnnotatedType,
  PrimaryAnnotatedType,
  SecondaryAnnotatedType,
} from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { Themes } from '@/components/microstory/theme';
import { Scatterplot } from '@/components/scatterplot';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { StoryMap } from '@/components/world-map/story';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import is from '@sindresorhus/is';
import { useState } from 'react';

type MentalBurdensChapter1Props = {
  data: MentalBurdensData;
};

export function MentalBurdensChapter1(props: MentalBurdensChapter1Props) {
  const { data } = props;

  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const section1ChartData1 = {
    label: t({
      message: '% reporting often feeling worried, nervous or anxious',
      id: 'mental-burdens.chapter-1.section-1.chart-top-label',
    }),
    data: [
      {
        label: youngerGeneration(i18n),
        value: data.often_worried.younger,
      },
    ],
  };

  const section1ChartData2 = {
    data: [
      {
        label: olderGeneration(i18n),
        value: data.often_worried.older,
      },
    ],
  };

  const [sunriseChartData, setSunriseChartData] = useState<SunriseChartData>();
  const handleSunriseChartStepEnter = (e: OnStepEnterExitArgs<SunriseChartData>) => {
    if (e.data) {
      setSunriseChartData(e.data);
    }
  };

  const [highlightedIds, setHighlightedIds] = useState<CountryIsoCode[]>();
  const scatterplotData = (
    Object.entries(data.often_worried_country_breakdown) as [CountryIsoCode, AgeGroupBreakdown][]
  ).map(([key, value]) => ({
    id: key,
    label: countryIdToName(key),
    ...value,
  }));

  const handleScatterplotStepEnter = (evt: OnStepEnterExitArgs<CountryIsoCode[]>) => {
    if (is.array(evt.data, is.string)) {
      setHighlightedIds(evt.data);
    }
  };

  return (
    <>
      <ScrollRoot>
        <ScrollGraphic>
          <SunriseChart data={sunriseChartData} formatValue={formatPercentage} />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleSunriseChartStepEnter} offset={0.7} hasGraphic>
          <ScrollStep data={[section1ChartData1]}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-1.text-1">
                On average, just over a third of{' '}
                <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> (
                {formatPercentage(data.often_worried.younger)}) say they frequently experience
                worry, anxiety or nervousness.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={[section1ChartData1, section1ChartData2]}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-1.text-2">
                That compares to {formatPercentage(data.often_worried.older)} of{' '}
                <SecondaryAnnotatedType>older people.</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <StoryMap
            theme={Themes.lime}
            highlightedCountries={['US', 'FR', 'DE', 'LB', 'UA', 'BD']}
          />
        </ScrollGraphic>
        <ScrollSteps>
          <ScrollStep>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.text">
                But the <AnnotatedType>generational split</AnnotatedType> with anxiety isn't the
                same in <PrimaryAnnotatedType>all countries&hellip;</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <Scatterplot
            data={scatterplotData}
            getXValue={(d) => d.older}
            getYValue={(d) => d.younger}
            highlightedIds={highlightedIds}
            idsAlignment={{ UA: 'top', BD: 'top' }}
            topAxisLabel={t({
              message: 'More 15–24 year-olds report often feeling worried, nervous or anxious',
              id: 'mental-burdens.chapter-1.section-2.top-axis-label',
            })}
            bottomAxisLabel={t({
              message: 'More 40+ year-olds report often feeling worried, nervous or anxious',
              id: 'mental-burdens.chapter-1.section-2.bottom-axis-label',
            })}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleScatterplotStepEnter} offset={0.5} hasGraphic>
          <ScrollStep data={['US']}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-2.text-1">
                Younger generations are relatively more anxious in the{' '}
                <PrimaryAnnotatedType>{countryIdToName('US')}&hellip;</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['FR']}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-2.text-2">
                <PrimaryAnnotatedType>&hellip;{countryIdToName('FR')}&hellip;</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['DE']}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-2.text-3">
                and <PrimaryAnnotatedType>{countryIdToName('DE')}.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['LB']}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-2.text-4">
                Whereas older generations are relatively more anxious in{' '}
                <PrimaryAnnotatedType>{countryIdToName('LB')}...</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['UA']}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-2.text-5">
                <PrimaryAnnotatedType>&hellip;{countryIdToName('UA')}&hellip;</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={['BD']}>
            <StoryText>
              <Trans id="mental-burdens.chapter-1.section-2.text-6">
                and <PrimaryAnnotatedType>{countryIdToName('BD')}.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
